import { AssetDestination } from '~/components/send-all-assets-to/SendAllAssetsTo';

import { POSTHOG_TRACKING_EVENTS, PosthogTrackingSource } from '../constants';
import { usePostHog } from '../usePostHog';

export const usePosthogEvents = () => {
  const { captureTrackingEvent } = usePostHog();

  const trackDashboardOpen = (source: PosthogTrackingSource) =>
    captureTrackingEvent({
      eventName: POSTHOG_TRACKING_EVENTS.DASHBOARD_OPEN,
      properties: {
        source,
      },
    });

  const trackChartOpen = (source: PosthogTrackingSource, type?: string) =>
    captureTrackingEvent({
      eventName: POSTHOG_TRACKING_EVENTS.CHART_OPEN,
      properties: {
        source,
        type,
      },
    });

  const trackEventsDrawerOpen = (source: PosthogTrackingSource, type: string) =>
    captureTrackingEvent({
      eventName: POSTHOG_TRACKING_EVENTS.EVENTS_DRAWER_OPEN,
      properties: {
        source,
        type,
      },
    });

  const trackTliDrawerOpen = (source: PosthogTrackingSource, type: string) =>
    captureTrackingEvent({
      eventName: POSTHOG_TRACKING_EVENTS.TLI_DRAWER_OPEN,
      properties: {
        source,
        type,
      },
    });

  const trackAoDrawerOpen = (source: PosthogTrackingSource) =>
    captureTrackingEvent({
      eventName: POSTHOG_TRACKING_EVENTS.AO_DRAWER_OPEN,
      properties: {
        source,
      },
    });

  const trackMarketSummaryOpen = (
    source: PosthogTrackingSource,
    type: string
  ) =>
    captureTrackingEvent({
      eventName: POSTHOG_TRACKING_EVENTS.MARKET_SUMMARY_OPEN,
      properties: {
        source,
        type,
      },
    });

  const trackEarningsInsightOpen = (
    source: PosthogTrackingSource,
    type: string
  ) =>
    captureTrackingEvent({
      eventName: POSTHOG_TRACKING_EVENTS.EARNINGS_INSIGHT_OPEN,
      properties: {
        source,
        type,
      },
    });

  const trackAlfredQuerySent = (source: PosthogTrackingSource, type: string) =>
    captureTrackingEvent({
      eventName: POSTHOG_TRACKING_EVENTS.QUERY_SENT,
      properties: {
        source,
        type,
      },
    });

  const trackWatchlistOpen = (source: PosthogTrackingSource) =>
    captureTrackingEvent({
      eventName: POSTHOG_TRACKING_EVENTS.WATCHLIST_OPEN,
      properties: {
        source,
      },
    });

  const trackWatchlistCreate = (source: PosthogTrackingSource) =>
    captureTrackingEvent({
      eventName: POSTHOG_TRACKING_EVENTS.WATCHLIST_CREATE,
      properties: {
        source,
      },
    });

  const trackSearchOpen = (source: PosthogTrackingSource) =>
    captureTrackingEvent({
      eventName: POSTHOG_TRACKING_EVENTS.SEARCH_OPEN,
      properties: {
        source,
      },
    });

  const trackAssetOpen = (source: PosthogTrackingSource) =>
    captureTrackingEvent({
      eventName: POSTHOG_TRACKING_EVENTS.ASSET_OPEN,
      properties: {
        source,
      },
    });

  const trackThemeOpen = (source: PosthogTrackingSource) =>
    captureTrackingEvent({
      eventName: POSTHOG_TRACKING_EVENTS.THEME_OPEN,
      properties: {
        source,
      },
    });

  const trackNewsOpen = (source: PosthogTrackingSource) =>
    captureTrackingEvent({
      eventName: POSTHOG_TRACKING_EVENTS.NEWS_OPEN,
      properties: {
        source,
      },
    });

  const trackInsightsOpen = (source: PosthogTrackingSource) =>
    captureTrackingEvent({
      eventName: POSTHOG_TRACKING_EVENTS.INSIGHTS_OPEN,
      properties: {
        source,
      },
    });

  const trackScreenerOpen = (source: PosthogTrackingSource) =>
    captureTrackingEvent({
      eventName: POSTHOG_TRACKING_EVENTS.SCREENER_OPEN,
      properties: {
        source,
      },
    });

  const trackScreenerCreate = (source: PosthogTrackingSource) =>
    captureTrackingEvent({
      eventName: POSTHOG_TRACKING_EVENTS.SCREENER_CREATE,
      properties: {
        source,
      },
    });

  const trackSendAllAssets = (
    source: PosthogTrackingSource,
    destination: AssetDestination
  ) =>
    captureTrackingEvent({
      eventName: POSTHOG_TRACKING_EVENTS.SEND_ALL_ASSETS,
      properties: {
        source,
        destination,
      },
    });

  const trackNewsFilter = (
    source: PosthogTrackingSource,
    type: 'most_read' | 'watchlist'
  ) =>
    captureTrackingEvent({
      eventName: POSTHOG_TRACKING_EVENTS.NEWS_FILTER_SELECTED,
      properties: {
        source,
        type,
      },
    });

  const trackWatchlistTabSelected = (
    source: PosthogTrackingSource,
    type: string
  ) =>
    captureTrackingEvent({
      eventName: POSTHOG_TRACKING_EVENTS.WATCHLIST_TAB_SELECTED,
      properties: {
        source,
        type,
      },
    });

  const trackInsightsDrawerOpen = (
    source: PosthogTrackingSource,
    type: 'simple' | 'full',
    otherProperties: Record<string, unknown> = {}
  ) =>
    captureTrackingEvent({
      eventName: POSTHOG_TRACKING_EVENTS.INSIGHTS_DRAWER_OPEN,
      properties: {
        ...otherProperties,
        source,
        type,
      },
    });

  return {
    trackAssetOpen,
    trackThemeOpen,
    trackWatchlistOpen,
    trackWatchlistCreate,
    trackSearchOpen,
    trackNewsOpen,
    trackInsightsOpen,
    trackScreenerOpen,
    trackScreenerCreate,
    trackSendAllAssets,
    trackNewsFilter,
    trackDashboardOpen,
    trackChartOpen,
    trackEventsDrawerOpen,
    trackTliDrawerOpen,
    trackAoDrawerOpen,
    trackMarketSummaryOpen,
    trackAlfredQuerySent,
    trackWatchlistTabSelected,
    trackInsightsDrawerOpen,
    trackEarningsInsightOpen,
  };
};
